// Simple registry shim to resolve shader source by name
import { createId } from '../../render/utils/node';
import { getShader, getShaderMeta } from '../shaders/registry';
import { getCacheByType } from './raster';

function isString(value) {
  return typeof value === 'string';
}

export function isShaderGradientFill(fill) {
  if (!isString(fill)) return false;
  return fill.trim().toLowerCase().startsWith('dynamic-shader(');
}

function parseNumber(val) {
  if (typeof val === 'number') return val;
  const n = parseFloat(String(val).trim());
  return Number.isFinite(n) ? n : null;
}

function parseBoolean(val) {
  if (typeof val === 'boolean') return val;
  const v = String(val).trim().toLowerCase();
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

function parseColor(str) {
  const s = String(str).trim();
  // hex #rgb/#rgba/#rrggbb/#rrggbbaa
  if (/^#([0-9a-f]{3,8})$/i.test(s)) {
    return toRgba(normHex(s));
  }
  // rgba()/rgb()
  const m = s.match(/^rgba?\s*\(([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(',').map((p) => p.trim());
    const r = parseNumber(parts[0]) || 0;
    const g = parseNumber(parts[1]) || 0;
    const b = parseNumber(parts[2]) || 0;
    const a = parts[3] != null ? parseNumber(parts[3]) : 1;
    return { r: clamp01(r / 255), g: clamp01(g / 255), b: clamp01(b / 255), a: clamp01(a ?? 1) };
  }
  // named colors fallback to black
  return { r: 0, g: 0, b: 0, a: 1 };
}

function normHex(hex) {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (h.length === 4) {
    // rgba #rgba -> #rrggbbaa
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length === 6) h = h + 'ff';
  return `#${h}`;
}

function toRgba(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = parseInt(h.slice(6, 8), 16) / 255;
  return { r: r / 255, g: g / 255, b: b / 255, a: clamp01(a) };
}

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function parseProps(propsStr) {
  const props = {};
  if (!propsStr) return props;
  const pairs = propsStr
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  for (const pair of pairs) {
    const idx = pair.indexOf('=');
    if (idx === -1) continue;
    const key = pair.slice(0, idx).trim();
    const raw = pair.slice(idx + 1).trim();
    if (!key) continue;
    if (key.toLowerCase() === 'colors') {
      const colors = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map(parseColor);
      props.colors = colors;
      continue;
    }
    // boolean
    const boolVal = parseBoolean(raw);
    if (boolVal !== null) {
      props[key] = boolVal;
      continue;
    }
    // number
    const numVal = parseNumber(raw);
    if (numVal !== null) {
      props[key] = numVal;
      continue;
    }
    // default string
    props[key] = raw;
  }
  return props;
}

function getParsedCache(renderContext) {
  return getCacheByType(renderContext, 'shaderFillParsed');
}

function parseShaderGradient(renderContext, fill) {
  const cache = getParsedCache(renderContext);

  // Cache parsed results since parsing is expensive
  if (cache.has(fill)) {
    return cache.get(fill);
  }

  // dynamic-shader(Name, key=val; key=val)
  const m = fill.match(/dynamic-shader\s*\(\s*([^,\s)]+)\s*(?:,\s*(.*))?\)\s*$/i);
  if (!m) {
    cache.set(fill, null);
    return null;
  }

  const name = m[1].trim();
  const propsStr = (m[2] || '').trim();
  const result = { name, props: parseProps(propsStr) };

  cache.set(fill, result);
  return result;
}

function createOffscreenCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(width));
  canvas.height = Math.max(1, Math.floor(height));
  return canvas;
}

// Standardize size to improve canvas/context reuse while keeping visual fidelity
function getStandardizedSize(size) {
  const standardSizes = [
    32, 48, 64, 80, 96, 112, 128, 144, 160, 176, 192, 208, 224, 240, 256, 288, 320, 352, 384, 416,
    448, 480, 512, 576, 640, 704, 768, 832, 896, 960, 1024, 1152, 1280, 1408, 1536, 1664, 1792,
    1920, 2048, 2304, 2560, 2816, 3072, 3328, 3584, 3840, 4096,
  ];
  for (let i = 0; i < standardSizes.length; i++) {
    if (standardSizes[i] >= size) {
      const diff = standardSizes[i] - size;
      const diffPercentage = diff / size;
      if (diffPercentage < 0.05) return size;
      return standardSizes[i];
    }
  }
  return size;
}

// Reuse a dedicated 2D canvas for animated shaders to minimize allocations
function getAnimated2DCanvas(renderContext, width, height) {
  const pool = getCacheByType(renderContext, 'shaderFill2DAnimated');
  const stdW = Math.max(1, Math.floor(getStandardizedSize(width)));
  const stdH = Math.max(1, Math.floor(getStandardizedSize(height)));
  const sizeKey = `${stdW}_${stdH}`;
  let entry = pool.get(sizeKey);
  if (!entry) {
    // Prefer global canvas pool when available
    const pooled = renderContext.providers?.canvas?.createCanvas
      ? renderContext.providers.canvas.createCanvas(stdW, stdH)
      : null;
    const c = pooled ? pooled.canvas : createOffscreenCanvas(stdW, stdH);
    entry = { canvas: c };
    entry = { canvas: c };
    pool.set(sizeKey, entry);
  } else {
    // Ensure size matches (resize only when required)
    const c = entry.canvas;
    if (c.width !== stdW || c.height !== stdH) {
      c.width = stdW;
      c.height = stdH;
    }
  }
  return entry.canvas;
}

// Prefer a single shared WebGL context to avoid exceeding browser limits
function getSharedGlContext(renderContext) {
  const cache = getCacheByType(renderContext, 'shaderFillGlShared');
  let entry = cache.get('shared');
  if (!entry) {
    const c = document.createElement('canvas');
    const gl =
      c.getContext('webgl', {
        premultipliedAlpha: true,
        alpha: true,
        preserveDrawingBuffer: true,
      }) ||
      c.getContext('experimental-webgl', {
        premultipliedAlpha: true,
        alpha: true,
        preserveDrawingBuffer: true,
      }) ||
      c.getContext('webgl2', {
        premultipliedAlpha: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
    if (!gl) return { canvas: null, gl: null };
    gl.__contextId = createId();
    const clearShaderFillCaches = () => {
      try {
        const programCache = getCacheByType(renderContext, 'shaderFillProgram');
        const compileCache = getCacheByType(renderContext, 'shaderFillCompile');
        programCache.clear();
        compileCache.clear();
      } catch {}
    };
    if (!gl.__handlersAttached) {
      c.addEventListener(
        'webglcontextlost',
        (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          gl.__lost = true;
          clearShaderFillCaches();
        },
        false,
      );
      c.addEventListener(
        'webglcontextrestored',
        () => {
          gl.__lost = false;
          gl.__contextId = createId();
          clearShaderFillCaches();
        },
        false,
      );
      gl.__handlersAttached = true;
    }
    entry = { canvas: c, gl };
    cache.set('shared', entry);
  }
  return entry;
}

function getShaderCompileCache(renderContext) {
  return getCacheByType(renderContext, 'shaderFillCompile');
}

function compileShader(renderContext, gl, type, source) {
  const cache = getShaderCompileCache(renderContext);
  const cacheKey = `${gl.__contextId || gl}:${type}:${source}`;

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }

  cache.set(cacheKey, shader);
  return shader;
}

function linkProgram(renderContext, gl, vsSource, fsSource) {
  const vs = compileShader(renderContext, gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(renderContext, gl, gl.FRAGMENT_SHADER, fsSource);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link error: ${info}`);
  }
  return program;
}

// Enhanced program cache with instance tracking
class ProgramInstance {
  constructor(gl, program, buffer) {
    this.gl = gl;
    this.program = program;
    this.buffer = buffer;
    this.uniformLocations = new Map();
    this.attributeLocations = new Map();
    this.lastUsed = Date.now();
  }

  getUniformLocation(name) {
    if (!this.uniformLocations.has(name)) {
      const loc = this.gl.getUniformLocation(this.program, name);
      this.uniformLocations.set(name, loc);
    }
    return this.uniformLocations.get(name);
  }

  getAttributeLocation(name) {
    if (!this.attributeLocations.has(name)) {
      const loc = this.gl.getAttribLocation(this.program, name);
      this.attributeLocations.set(name, loc);
    }
    return this.attributeLocations.get(name);
  }

  use() {
    this.lastUsed = Date.now();
    this.gl.useProgram(this.program);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffer);

    const posLoc = this.getAttributeLocation('a_position');
    if (posLoc !== -1) {
      this.gl.enableVertexAttribArray(posLoc);
      this.gl.vertexAttribPointer(posLoc, 2, this.gl.FLOAT, false, 0, 0);
    }
  }

  dispose() {
    if (this.program) {
      this.gl.deleteProgram(this.program);
      this.program = null;
    }
    if (this.buffer) {
      this.gl.deleteBuffer(this.buffer);
      this.buffer = null;
    }
    this.uniformLocations.clear();
    this.attributeLocations.clear();
  }
}

function getProgramCache(renderContext) {
  return getCacheByType(renderContext, 'shaderFillProgram');
}

function createFullScreenProgram(renderContext, gl, frag) {
  const vsSource = `
attribute vec2 a_position;
varying vec2 vUv;
void main() {
  vUv = (a_position + 1.0) * 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

  const cache = getProgramCache(renderContext);
  const contextId = gl.__contextId || gl;
  const cacheKey = `${contextId}_${frag}`; // Include GL context in cache key
  const cached = cache.get(cacheKey);

  // Fast-path reuse without gl.isProgram validation
  if (cached && cached.gl === gl && cached.program && !gl.isContextLost() && !gl.__lost) {
    try {
      cached.use();
      return cached;
    } catch {
      cached.dispose();
      cache.delete(cacheKey);
    }
  }

  // Clean up old instance if exists
  if (cached) {
    cached.dispose();
    cache.delete(cacheKey);
  }

  const program = linkProgram(renderContext, gl, vsSource, frag);
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  const verts = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);

  const instance = new ProgramInstance(gl, program, buffer);
  cache.set(cacheKey, instance);
  instance.use();

  return instance;
}

// Enhanced uniform setting with caching
function setUniformOptimized(instance, name, value) {
  const loc = instance.getUniformLocation(name);
  if (loc == null) return;

  const gl = instance.gl;
  if (typeof value === 'number') {
    gl.uniform1f(loc, value);
    return;
  }
  if (typeof value === 'boolean') {
    gl.uniform1i(loc, value ? 1 : 0);
    return;
  }
  if (Array.isArray(value)) {
    if (value.length === 2) gl.uniform2f(loc, value[0], value[1]);
    else if (value.length === 3) gl.uniform3f(loc, value[0], value[1], value[2]);
    else if (value.length === 4) gl.uniform4f(loc, value[0], value[1], value[2], value[3]);
    return;
  }
  if (value && typeof value === 'object' && 'r' in value && 'g' in value && 'b' in value) {
    gl.uniform3f(loc, value.r, value.g, value.b);
    return;
  }
}

function renderShaderToCanvas(renderContext, canvas, frag, uniforms) {
  const { canvas: glCanvas, gl } = getSharedGlContext(renderContext);
  if (!gl) throw new Error('WebGL not supported');

  // Check for context loss
  if (gl.isContextLost()) {
    throw new Error('WebGL context lost');
  }

  // Resize shared GL canvas to match target
  if (glCanvas.width !== canvas.width || glCanvas.height !== canvas.height) {
    glCanvas.width = canvas.width;
    glCanvas.height = canvas.height;
  }

  gl.viewport(0, 0, glCanvas.width, glCanvas.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  const instance = createFullScreenProgram(renderContext, gl, frag);

  // standard uniforms
  setUniformOptimized(instance, 'u_resolution', [glCanvas.width, glCanvas.height]);
  for (const [key, val] of Object.entries(uniforms || {})) {
    if (key === 'u_resolution') continue;
    setUniformOptimized(instance, key, val);
  }

  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // blit result to 2D target canvas
  const ctx = canvas.getContext('2d');
  const prevGCO = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = 'copy';
  ctx.drawImage(glCanvas, 0, 0, canvas.width, canvas.height);
  if (prevGCO !== 'copy') ctx.globalCompositeOperation = prevGCO;
}

function makeCacheKey({ shader, props, width, height, dpr, zoom, version }) {
  const colors = (props.colors || [])
    .map((c) => `${c.r.toFixed(4)}_${c.g.toFixed(4)}_${c.b.toFixed(4)}_${c.a.toFixed(4)}`)
    .join('|');
  const keys = Object.keys(props)
    .sort()
    .map((k) => `${k}=${String(props[k])}`)
    .join('&');
  return [shader, colors, keys, width, height, dpr, zoom, version].join('::');
}

function parseShaderUniforms(name, props, node, renderContext, animated) {
  const uniforms = {};

  // time uniform controlled by host
  uniforms.u_time = animated
    ? renderContext && renderContext.api && typeof renderContext.api.now === 'function'
      ? renderContext.api.now() / 1000
      : performance && performance.now
        ? performance.now() / 1000
        : Date.now() / 1000
    : 0;

  // angle and opacity
  const angle = props.angle != null ? (parseNumber(props.angle) * Math.PI) / 180 : null;
  if (angle != null) uniforms.u_angle = angle;

  uniforms.u_size = [Math.round(Math.abs(node.w)), Math.round(Math.abs(node.h))];
  // also provide pixel resolution for shaders relying on gl_FragCoord scale
  const dpr = renderContext.dpr || 1;
  const scaleProvider = renderContext.providers && renderContext.providers.scale;
  const scale = (scaleProvider && scaleProvider.scale) || 1;
  // During active zoom/pan, render at reduced quality to keep FPS
  const qualityFactor = scaleProvider && scaleProvider.scaling ? 0.6 : 1;
  const pixelScale = dpr * scale * qualityFactor;
  // Cap extreme sizes to avoid jank
  const MAX_DIM = 4096;
  let width = Math.max(1, Math.round(Math.abs(node.w) * pixelScale));
  let height = Math.max(1, Math.round(Math.abs(node.h) * pixelScale));
  width = Math.min(width, MAX_DIM);
  height = Math.min(height, MAX_DIM);
  // Standardize to improve cache reuse
  width = Math.max(1, Math.floor(getStandardizedSize(width)));
  height = Math.max(1, Math.floor(getStandardizedSize(height)));
  uniforms.u_resolution = [width, height];

  // colors (ensure defaults to avoid all-zero uniforms leading to black)
  const provided = props.colors || [];
  const defaults = [
    { r: 1, g: 0, b: 0, a: 1 },
    { r: 0, g: 1, b: 0, a: 1 },
    { r: 0, g: 0, b: 1, a: 1 },
    { r: 1, g: 1, b: 0, a: 1 },
    { r: 1, g: 0, b: 1, a: 1 },
    { r: 0, g: 1, b: 1, a: 1 },
  ];
  const palette = provided.length ? provided : defaults;
  const cap = Math.max(6, palette.length);
  for (let i = 0; i < cap; i++) {
    const c = palette[Math.min(i, palette.length - 1)];
    uniforms[`u_color_${i}`] = { r: c.r, g: c.g, b: c.b };
  }

  // map common scalar uniforms
  if (props.speed != null) uniforms.u_speed = parseNumber(props.speed) || 0;
  if (props.scale != null) uniforms.u_scale = parseNumber(props.scale) || 1;

  // unified random seed: accept 'seed' or 'u_seed', always provide u_seed (default 0)
  {
    const providedSeed =
      (props.seed != null ? parseNumber(props.seed) : null) ??
      (props.u_seed != null ? parseNumber(props.u_seed) : null);
    uniforms.u_seed = providedSeed != null ? providedSeed : 0;
  }

  // Build schema type map for advanced parsing (e.g., vec2/vec3/vec4)
  const meta = getShaderMeta(name);
  const schemaTypeByUniform = new Map();
  if (meta && Array.isArray(meta.schema)) {
    for (const entry of meta.schema) {
      if (!entry || entry.key == null) continue;
      let uniformName = String(entry.key);
      if (!uniformName.startsWith('u_')) {
        if (uniformName === 'speed') uniformName = 'u_speed';
        else if (uniformName === 'scale') uniformName = 'u_scale';
        else if (uniformName === 'seed') uniformName = 'u_seed';
      }
      schemaTypeByUniform.set(uniformName, entry.type);
    }
  }

  // Helper to coerce various inputs into fixed-length float vectors
  function coerceVec(value, n) {
    const out = new Array(n);
    if (Array.isArray(value)) {
      for (let i = 0; i < n; i++) {
        const num = parseNumber(value[i]);
        out[i] = Number.isFinite(num) ? num : 0;
      }
      return out;
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[()[\]]/g, ' ').trim();
      const parts = cleaned.split(/[,\s]+/).filter(Boolean);
      if (parts.length >= n) {
        for (let i = 0; i < n; i++) {
          const num = parseNumber(parts[i]);
          out[i] = Number.isFinite(num) ? num : 0;
        }
        return out;
      }
    }
    const num = parseNumber(value);
    if (num !== null) {
      for (let i = 0; i < n; i++) out[i] = num;
      return out;
    }
    return null;
  }

  // generic mapping: pass other props as uniforms, honoring schema types
  const reserved = new Set([
    'colors',
    'animated',
    'angle',
    'opacity',
    'speed',
    'scale',
    'seed',
    'u_seed',
  ]);
  for (const [k, v] of Object.entries(props)) {
    if (reserved.has(k)) continue;
    const uniformName = k.startsWith('u_') ? k : `u_${k}`;
    const type = schemaTypeByUniform.get(uniformName);
    if (type === 'vec2') {
      const vec = coerceVec(v, 2);
      if (vec) {
        uniforms[uniformName] = vec;
        continue;
      }
    } else if (type === 'vec3') {
      const vec = coerceVec(v, 3);
      if (vec) {
        uniforms[uniformName] = vec;
        continue;
      }
    } else if (type === 'vec4') {
      const vec = coerceVec(v, 4);
      if (vec) {
        uniforms[uniformName] = vec;
        continue;
      }
    }
    if (typeof v === 'boolean') {
      uniforms[uniformName] = v;
      continue;
    }
    const num = parseNumber(v);
    if (num !== null) {
      uniforms[uniformName] = num;
    }
  }

  // ensure sane defaults for common uniforms when not explicitly provided
  if (uniforms.u_speed == null) uniforms.u_speed = 1;

  // Apply schema defaults for missing uniforms (numbers, booleans, and vec types)
  if (meta && Array.isArray(meta.schema)) {
    for (const entry of meta.schema) {
      if (!entry || entry.key == null) continue;
      const key = String(entry.key);
      if (key === 'colors' || key === 'animated' || key === 'angle' || key === 'opacity') continue;
      let uniformName = key;
      if (!uniformName.startsWith('u_')) {
        if (key === 'speed') uniformName = 'u_speed';
        else if (key === 'scale') uniformName = 'u_scale';
        else if (key === 'seed') uniformName = 'u_seed';
      }
      if (uniforms[uniformName] == null && entry.default != null) {
        if (typeof entry.default === 'number' || typeof entry.default === 'boolean') {
          uniforms[uniformName] = entry.default;
        } else if (
          (entry.type === 'vec2' || entry.type === 'vec3' || entry.type === 'vec4') &&
          Array.isArray(entry.default)
        ) {
          const n = entry.type === 'vec2' ? 2 : entry.type === 'vec3' ? 3 : 4;
          const vec = [];
          for (let i = 0; i < n; i++) vec[i] = parseNumber(entry.default[i]) || 0;
          uniforms[uniformName] = vec;
        }
      }
    }
  }

  // Safety guards to avoid invalid math in shaders (e.g., division by zero)
  if (
    uniforms.u_falloff != null &&
    (!Number.isFinite(uniforms.u_falloff) || uniforms.u_falloff <= 0)
  ) {
    uniforms.u_falloff = 1;
  }
  if (
    uniforms.u_smoothness != null &&
    (!Number.isFinite(uniforms.u_smoothness) || uniforms.u_smoothness <= 0)
  ) {
    uniforms.u_smoothness = 0.01;
  }

  return uniforms;
}

export function renderShaderFill(renderContext, fill, node) {
  try {
    const parsed = parseShaderGradient(renderContext, fill);
    if (!parsed) return false;
    const { name, props } = parsed;

    const shader = getShader(name);
    if (!shader) return false;

    const scaleProvider = renderContext.providers && renderContext.providers.scale;
    const isScaling = !!(scaleProvider && scaleProvider.scaling);
    const caches = getCacheByType(renderContext, 'shaderFill');
    const existing = caches.get(node.id);

    const animated = props.animated === true;
    const opacity = props.opacity != null ? clamp01(parseNumber(props.opacity)) : null;
    const uniforms = parseShaderUniforms(name, props, node, renderContext, animated);

    // While scaling, reuse existing offscreen to avoid frequent resolution changes that cause jitter
    if (isScaling && existing && existing.canvas) {
      if (animated) {
        const offscreen = existing.canvas;
        uniforms.u_resolution = [offscreen.width, offscreen.height];
        try {
          renderShaderToCanvas(renderContext, offscreen, shader.frag, uniforms);
        } catch {
          return false;
        }
        drawIntoContext(renderContext, node, offscreen, opacity);
        if (renderContext.api && typeof renderContext.api.markNeedRender === 'function') {
          renderContext.api.markNeedRender();
        }
        return true;
      } else {
        drawIntoContext(renderContext, node, existing.canvas, opacity);
        return true;
      }
    }

    const dpr = renderContext.dpr || 1;
    const scale = (scaleProvider && scaleProvider.scale) || 1;
    const qualityFactor = scaleProvider && scaleProvider.scaling ? 0.4 : 1;
    const pixelScale = dpr * scale * qualityFactor;
    const MAX_DIM = 4096;
    let width = Math.max(1, Math.round(Math.abs(node.w) * pixelScale));
    let height = Math.max(1, Math.round(Math.abs(node.h) * pixelScale));
    width = Math.min(width, MAX_DIM);
    height = Math.min(height, MAX_DIM);
    width = Math.max(1, Math.floor(getStandardizedSize(width)));
    height = Math.max(1, Math.floor(getStandardizedSize(height)));

    const cacheKey = makeCacheKey({
      shader: name,
      props,
      width,
      height,
      dpr: renderContext.dpr,
      zoom: scale,
    });

    // For animated shaders, always check if we need to re-render
    if (!animated && existing && existing.key === cacheKey && existing.canvas) {
      drawIntoContext(renderContext, node, existing.canvas, opacity);
      return true;
    }

    let offscreen;
    if (animated) {
      // Reuse existing canvas for animated shaders if possible
      if (
        existing &&
        existing.canvas &&
        existing.key === cacheKey &&
        existing.canvas.width === width &&
        existing.canvas.height === height
      ) {
        offscreen = existing.canvas;
      } else {
        offscreen = getAnimated2DCanvas(renderContext, width, height);
      }
    } else {
      // Non-animated: create a dedicated canvas for caching this frame
      // Clean up old cache entry if it exists
      if (existing?.dispose) {
        existing.dispose();
      }
      const pooled = renderContext.providers?.canvas?.createCanvas
        ? renderContext.providers.canvas.createCanvas(width, height)
        : null;
      offscreen = pooled ? pooled.canvas : createOffscreenCanvas(width, height);
    }
    try {
      renderShaderToCanvas(renderContext, offscreen, shader.frag, uniforms);
    } catch {
      // fail soft: do not break pipeline
      return false;
    }

    const cache = animated
      ? {
          key: cacheKey,
          canvas: offscreen,
          // Animated canvases are pooled; do not shrink to 0 to avoid GC churn
          dispose: () => {},
        }
      : {
          key: cacheKey,
          canvas: offscreen,
          dispose: () => {
            if (!offscreen) return;
            if (typeof offscreen.release === 'function') {
              // Return to pool when available
              offscreen.release();
            } else {
              // Fallback: shrink to zero to hint GC
              offscreen.width = 0;
              offscreen.height = 0;
            }
          },
        };

    caches.set(node.id, cache);
    drawIntoContext(renderContext, node, offscreen, opacity);

    // Mark for re-render if animated
    if (animated && renderContext.api && typeof renderContext.api.markNeedRender === 'function') {
      renderContext.api.markNeedRender();
    }

    return true;
  } catch {
    return false;
  }
}

function drawIntoContext(renderContext, node, offscreen, opacity) {
  const ctx = renderContext.context;
  ctx.save();
  // clip to current path which is already defined by caller
  ctx.clip();
  const originalAlpha = ctx.globalAlpha;
  if (node.opacity != null) ctx.globalAlpha *= node.opacity;
  if (opacity != null) ctx.globalAlpha *= opacity;
  ctx.drawImage(offscreen, 0, 0, offscreen.width, offscreen.height, node.x, node.y, node.w, node.h);
  ctx.globalAlpha = originalAlpha;
  ctx.restore();
}

// helper for graceful degrade in callers
export function extractShaderColors(fill) {
  try {
    // Create a minimal renderContext for parsing
    const tempRenderContext = { cache: {} };
    const parsed = parseShaderGradient(tempRenderContext, fill);
    if (!parsed || !parsed.props || !Array.isArray(parsed.props.colors)) return [];
    const css = [];
    for (const c of parsed.props.colors) {
      const r = Math.round(c.r * 255);
      const g = Math.round(c.g * 255);
      const b = Math.round(c.b * 255);
      const a = c.a != null ? c.a : 1;
      if (a >= 1) css.push(`rgb(${r},${g},${b})`);
      else css.push(`rgba(${r},${g},${b},${a.toFixed(3)})`);
    }
    return css;
  } catch {
    return [];
  }
}

// Lightweight helper: render a dynamic-shader fill into an offscreen canvas for an arbitrary size
// Returns { canvas, animated } or null on failure. The returned canvas size is in device pixels; caller can scale when drawing.
export function getShaderFillCanvas(renderContext, fill, width, height) {
  try {
    if (!isShaderGradientFill(fill)) return null;
    const parsed = parseShaderGradient(renderContext, fill);
    if (!parsed) return null;
    const { name, props } = parsed;
    const shader = getShader(name);
    if (!shader) return null;

    const fakeNode = {
      x: 0,
      y: 0,
      w: Math.max(1, Math.floor(width)),
      h: Math.max(1, Math.floor(height)),
    };
    const animated = props.animated === true;
    const uniforms = parseShaderUniforms(name, props, fakeNode, renderContext, animated);

    // Create/reuse offscreen 2D canvas according to animated flag and target resolution
    const targetW = uniforms.u_resolution?.[0] || Math.max(1, Math.floor(width));
    const targetH = uniforms.u_resolution?.[1] || Math.max(1, Math.floor(height));

    let offscreen;
    if (animated) {
      offscreen = getAnimated2DCanvas(renderContext, targetW, targetH);
    } else {
      const pooled = renderContext.providers?.canvas?.createCanvas
        ? renderContext.providers.canvas.createCanvas(targetW, targetH)
        : null;
      offscreen = pooled ? pooled.canvas : createOffscreenCanvas(targetW, targetH);
    }

    // Ensure canvas matches resolution
    if (offscreen.width !== targetW || offscreen.height !== targetH) {
      offscreen.width = targetW;
      offscreen.height = targetH;
    }

    renderShaderToCanvas(renderContext, offscreen, shader.frag, uniforms);

    if (animated && renderContext.api && typeof renderContext.api.markNeedRender === 'function') {
      renderContext.api.markNeedRender();
    }

    return { canvas: offscreen, animated };
  } catch {
    return null;
  }
}
